import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { uploadJobPDF } from './pdf-storage';

const BRAND_BROWN = [139, 69, 19];
const BRAND_LIGHT = [200, 160, 110];
const WHITE       = [255, 255, 255];
const LIGHT_GRAY  = [240, 240, 240];
const MID_GRAY    = [180, 180, 180];
const DARK        = [40, 40, 40];
const LABEL_COLOR = [100, 60, 20];
const TEAL        = [0, 105, 92];
const TEAL_BG     = [224, 247, 242];
const TEAL_TEXT   = [0, 77, 64];

const PAGE_W   = 215.9;
const PAGE_H   = 279.4;
const MARGIN   = 14;
const HEADER_H = 38;
const FOOTER_H = 12;
const SAFE_Y   = PAGE_H - FOOTER_H - 4;

// Height of the totals block (subtotal row + tax row + grand total row + spacing)
const TOTALS_H = 32;
// Height of the after-table section (payment terms + thank you + mailing)
const AFTER_H  = 46;
// Height of the elegant continuation notice shown at bottom of page 1 on multi-page estimates
const SLIM_BAR_H = 18;
// How many mm to reserve at the bottom of every page for the table margin
// Must fit: totals block so it never gets orphaned onto a new page alone
const TABLE_BOTTOM_RESERVE = FOOTER_H + 6 + TOTALS_H;

const LOGO_URL = '/brand/twp-logo.png';
let _logo = null;
async function getLogo() {
  if (_logo) return _logo;
  try {
    const res  = await fetch(LOGO_URL);
    const blob = await res.blob();
    return new Promise(r => {
      const fr = new FileReader();
      fr.onloadend = () => { _logo = fr.result; r(_logo); };
      fr.onerror   = () => r(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

function formatDate(dateStr) {
  if (!dateStr) return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatPhoneNumber(raw) {
  if (!raw) return '';
  const d = raw.replace(/\D/g, '');
  if (d.length === 10) return '(' + d.slice(0,3) + ') ' + d.slice(3,6) + '-' + d.slice(6);
  return raw;
}

function fmtMoney(n) {
  return '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ─────────────────────────────────────────────────────────────────────────
   HEADER  (full — page 1)
───────────────────────────────────────────────────────────────────────── */
function drawHeader(doc, estimate, logo, pageNum, totalPages) {
  doc.setFillColor(...WHITE);
  doc.rect(0, 0, PAGE_W, HEADER_H, 'F');
  doc.setDrawColor(...BRAND_LIGHT); doc.setLineWidth(3);
  doc.line(0, 0, 0, HEADER_H);
  doc.setLineWidth(0.8);
  doc.line(0, HEADER_H, PAGE_W, HEADER_H);
  doc.setLineWidth(0.3);

  if (logo) { try { doc.addImage(logo, 'PNG', MARGIN, 4, 18, 18); } catch {} }

  doc.setTextColor(...BRAND_BROWN); doc.setFontSize(13); doc.setFont('helvetica', 'bold');
  doc.text('The Window Place LLC', MARGIN + 22, 12);
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  doc.text('1300 E 4th Ave  \u2022  Milbank, SD 57252  \u2022  605-432-1834  \u2022  thewindowplacellc.com', MARGIN + 22, 17.5);

  doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(...TEAL);
  doc.text('ESTIMATE', PAGE_W - MARGIN, 14, { align: 'right' });

  doc.setDrawColor(...LIGHT_GRAY); doc.setLineWidth(0.3);
  doc.line(MARGIN, 22, PAGE_W - MARGIN, 22);

  const c      = estimate.customer || {};
  const fields = [
    ['Customer',     c.name || '\u2014'],
    ['Date',         formatDate(estimate.orderDate)],
    ['Prepared By',  estimate._repName  || 'A.J. Gingerich'],
    ['Phone',        estimate._repPhone || '605-432-1834'],
    ['Page',         pageNum + ' of ' + totalPages],
  ];
  const fieldW = (PAGE_W - MARGIN * 2) / fields.length;
  fields.forEach(([label, value], i) => {
    const fx = MARGIN + i * fieldW;
    doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...LABEL_COLOR);
    doc.text(label + ':', fx, 27);
    doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
    let v = value;
    const maxW = fieldW - 4;
    while (doc.getTextWidth(v) > maxW && v.length > 3) v = v.slice(0, -1);
    doc.text(v, fx, 33);
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   CONTINUATION HEADER  (page 2+)
───────────────────────────────────────────────────────────────────────── */
function drawContinuationHeader(doc, estimate, logo, pageNum, totalPages) {
  doc.setFillColor(...WHITE); doc.rect(0, 0, PAGE_W, 22, 'F');
  doc.setDrawColor(...BRAND_LIGHT); doc.setLineWidth(3); doc.line(0, 0, 0, 22);
  doc.setLineWidth(0.8); doc.line(0, 22, PAGE_W, 22); doc.setLineWidth(0.3);

  if (logo) { try { doc.addImage(logo, 'PNG', MARGIN, 3, 12, 12); } catch {} }

  doc.setTextColor(...BRAND_BROWN); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text('The Window Place LLC', MARGIN + 16, 11);

  const c = estimate.customer || {};
  doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  doc.text('Estimate for: ' + (c.name || '\u2014') + '  \u2022  ' + formatDate(estimate.orderDate), MARGIN + 16, 17);

  doc.setTextColor(...TEAL); doc.setFontSize(9); doc.setFont('helvetica', 'bold');
  doc.text('ESTIMATE (continued)  \u2014  Page ' + pageNum + ' of ' + totalPages, PAGE_W - MARGIN, 13, { align: 'right' });
}

/* ─────────────────────────────────────────────────────────────────────────
   PAGE FOOTER
───────────────────────────────────────────────────────────────────────── */
function drawFooter(doc, pageNum, totalPages) {
  const y = PAGE_H - FOOTER_H;
  doc.setFillColor(...WHITE); doc.rect(0, y, PAGE_W, FOOTER_H, 'F');
  doc.setDrawColor(...BRAND_LIGHT); doc.setLineWidth(3); doc.line(0, y, 0, PAGE_H);
  doc.setLineWidth(0.8); doc.line(0, y, PAGE_W, y); doc.setLineWidth(0.3);
  doc.setTextColor(...LABEL_COLOR); doc.setFontSize(7); doc.setFont('helvetica', 'normal');
  doc.text(
    'The Window Place LLC  \u2022  1300 E 4th Ave, Milbank, SD 57252  \u2022  605-432-1834  \u2022  aj@thewindowplacellc.com',
    PAGE_W / 2, y + 4.5, { align: 'center' }
  );
  const now     = new Date();
  const printed = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + '  ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  doc.setTextColor(...MID_GRAY); doc.setFontSize(6.5);
  doc.text('Printed: ' + printed + '  \u2022  Page ' + pageNum + ' of ' + totalPages, PAGE_W / 2, y + 9, { align: 'center' });
}

/* ─────────────────────────────────────────────────────────────────────────
   ELEGANT CONTINUATION NOTICE  (page 1 bottom on multi-page estimates)

   Design language: matches the estimate's clean, open aesthetic.
   No heavy fill. Uses a top rule in brand brown, a very light warm tint
   for the left panel, and a crisp teal-outlined grand total on the right.
   The result reads like a professional invoice, not a banner ad.
───────────────────────────────────────────────────────────────────────── */
function drawSlimTotalBar(doc, estimate) {
  const BAR_H = 18;
  const barY  = PAGE_H - FOOTER_H - 3 - BAR_H;
  const W     = PAGE_W - MARGIN * 2;
  const divX  = MARGIN + W - 72; // right panel is 72mm wide

  // ── Background: warm cream, thin border ────────────────────────────────
  doc.setFillColor(252, 248, 244);
  doc.setDrawColor(...BRAND_LIGHT);
  doc.setLineWidth(0.3);
  doc.roundedRect(MARGIN, barY, W, BAR_H, 2, 2, 'FD');

  // ── Brown top rule ──────────────────────────────────────────────────────
  doc.setFillColor(...BRAND_BROWN);
  doc.rect(MARGIN, barY, W, 1.5, 'F');

  // ── Brown left strip ────────────────────────────────────────────────────
  doc.setFillColor(...BRAND_BROWN);
  doc.rect(MARGIN, barY, 3.5, BAR_H, 'F');

  // ── LEFT: two lines of text, no arrows, no overlaps ────────────────────
  const lx = MARGIN + 8;

  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_BROWN);
  doc.text('CONTINUED ON NEXT PAGE', lx, barY + 7);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 55, 30);
  doc.text('Full itemized breakdown on following page', lx, barY + 14);

  // ── Vertical divider ────────────────────────────────────────────────────
  doc.setDrawColor(...BRAND_LIGHT);
  doc.setLineWidth(0.4);
  doc.line(divX, barY + 3, divX, barY + BAR_H - 3);

  // ── RIGHT: GRAND TOTAL label + amount ──────────────────────────────────
  const rx = divX + 5;

  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...TEAL_TEXT);
  doc.text('GRAND TOTAL', rx, barY + 7);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_BROWN);
  doc.text(
    fmtMoney(estimate.grandTotal || 0),
    MARGIN + W - 5, barY + 15,
    { align: 'right' }
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   CUSTOMER BOX
───────────────────────────────────────────────────────────────────────── */
function drawCustomerBox(doc, y, c, repName, repPhone, repEmail) {
  const boxH = 34;
  doc.setFillColor(240, 250, 248); doc.setDrawColor(...LIGHT_GRAY); doc.setLineWidth(0.3);
  doc.roundedRect(MARGIN, y, PAGE_W - MARGIN * 2, boxH, 2, 2, 'FD');

  const col2 = PAGE_W / 2 + 2;

  doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...TEAL_TEXT);
  doc.text('PREPARED FOR', MARGIN + 4, y + 5);
  doc.setFontSize(9.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
  doc.text(c.name || '', MARGIN + 4, y + 11);
  doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  let ly = y + 16;
  if (c.address) { doc.text(c.address, MARGIN + 4, ly); ly += 4; }
  const cityLine = [c.city, c.state, c.zip].filter(Boolean).join(', ');
  if (cityLine) { doc.text(cityLine, MARGIN + 4, ly); ly += 4; }
  if (c.phone)  { doc.text(c.phone,  MARGIN + 4, ly); ly += 4; }
  if (c.email)  {
    // Truncate with ellipsis if email would cross the box divider at PAGE_W/2.
    // Reserves a 4mm right gutter so it never touches the divider line.
    const emailMaxW = (PAGE_W / 2) - (MARGIN + 4) - 10;
    let emailText = c.email;
    if (doc.getTextWidth(emailText) > emailMaxW) {
      while (emailText.length > 1 && doc.getTextWidth(emailText + '…') > emailMaxW) {
        emailText = emailText.slice(0, -1);
      }
      emailText = emailText + '…';
    }
    doc.text(emailText, MARGIN + 4, ly);
  }

  doc.setDrawColor(...LIGHT_GRAY); doc.setLineWidth(0.3);
  doc.line(PAGE_W / 2, y + 3, PAGE_W / 2, y + boxH - 3);

  doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...TEAL_TEXT);
  doc.text('PREPARED BY', col2 + 4, y + 5);
  doc.setFontSize(9.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
  doc.text(repName, col2 + 4, y + 11);
  doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  doc.text('The Window Place LLC', col2 + 4, y + 16);
  doc.text(repPhone, col2 + 4, y + 20);
  doc.text(repEmail, col2 + 4, y + 24);

  return y + boxH + 4;
}

/* ─────────────────────────────────────────────────────────────────────────
   TOTALS BLOCK
───────────────────────────────────────────────────────────────────────── */
function drawTotals(doc, y, estimate) {
  const totW = 80;
  const totX = PAGE_W - MARGIN - totW;

  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  doc.text('Subtotal', totX, y);
  doc.setTextColor(...DARK);
  doc.text(fmtMoney(estimate.subtotal || 0), PAGE_W - MARGIN, y, { align: 'right' });
  y += 5.5;

  if (estimate.taxEnabled) {
    doc.setTextColor(...LABEL_COLOR);
    const taxLabel = estimate.taxRate === '6.2' ? 'Sales Tax' : 'Excise Tax';
    doc.text(taxLabel + ' (' + (estimate.taxRate || '2.041') + '%)', totX, y);
    doc.setTextColor(...DARK);
    doc.text(fmtMoney(estimate.taxAmount || 0), PAGE_W - MARGIN, y, { align: 'right' });
    y += 5.5;
  }

  doc.setDrawColor(...BRAND_LIGHT); doc.setLineWidth(0.4);
  doc.line(totX, y, PAGE_W - MARGIN, y);
  y += 3.5;

  doc.setFillColor(...TEAL_BG); doc.setDrawColor(...TEAL); doc.setLineWidth(0.3);
  doc.roundedRect(totX - 2, y - 1, totW + 4, 10, 2, 2, 'FD');
  doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...TEAL_TEXT);
  doc.text('GRAND TOTAL', totX + 2, y + 6);
  doc.setTextColor(...BRAND_BROWN);
  doc.text(fmtMoney(estimate.grandTotal || 0), PAGE_W - MARGIN - 1, y + 6, { align: 'right' });

  return y + 14;
}

/* ─────────────────────────────────────────────────────────────────────────
   NOTES
───────────────────────────────────────────────────────────────────────── */
function drawNotes(doc, y, estimate) {
  if (!estimate.notes) return y;
  const noteStartY = y;
  doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...TEAL_TEXT);
  doc.text('NOTES & TERMS', MARGIN + 4, y + 5);
  y += 8;
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
  const lines = doc.splitTextToSize('\u2022 ' + estimate.notes, PAGE_W - MARGIN * 2 - 8);
  lines.forEach(ln => { doc.text(ln, MARGIN + 4, y); y += 4.5; });
  doc.setFillColor(240, 250, 248); doc.setDrawColor(...LIGHT_GRAY); doc.setLineWidth(0.3);
  doc.roundedRect(MARGIN, noteStartY, PAGE_W - MARGIN * 2, y - noteStartY + 3, 2, 2, 'S');
  return y + 6;
}

/* ─────────────────────────────────────────────────────────────────────────
   AFTER-TABLE SECTION  (payment terms + thank you + mailing)
   Only drawn if there is enough room below the totals block.
───────────────────────────────────────────────────────────────────────── */
function drawAfterSection(doc, y, repName, repEmail, repCellPhone) {
  // Payment terms
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
  doc.text('Prices for Windows - 1/3 Down Payment', MARGIN, y); y += 4.5;
  doc.text('Doors - 1/2 Down Payment', MARGIN, y); y += 4.5;
  doc.setFont('helvetica', 'bold');
  doc.text('Balance Upon Completion for all Orders', MARGIN, y); y += 8;

  // Thank you
  const thanksY = y;
  doc.setFontSize(8.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
  doc.text('Thank you,', MARGIN, y); y += 5;
  doc.setFont('helvetica', 'bold'); doc.setTextColor(...BRAND_BROWN);
  let repLine = repName;
  if (repCellPhone) repLine += '  ' + formatPhoneNumber(repCellPhone) + ' Cell';
  doc.text(repLine, MARGIN, y); y += 4.5;
  doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  doc.text(repEmail, MARGIN, y);

  // Mailing address
  const rightX = PAGE_W / 2 + 20;
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
  doc.text('MAILING ADDRESS', rightX, thanksY);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  doc.text('The Window Place LLC', rightX, thanksY + 4.5);
  doc.text('PO BOX 472',          rightX, thanksY + 9);
  doc.text('Milbank, SD 57252',   rightX, thanksY + 13.5);

  y += 8;
  doc.setDrawColor(...LIGHT_GRAY); doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y); y += 4;
  doc.setFontSize(7); doc.setFont('helvetica', 'italic'); doc.setTextColor(...MID_GRAY);
  doc.text('Prices subject to change without notice', MARGIN, y);
}

/* ─────────────────────────────────────────────────────────────────────────
   TABLE BODY BUILDER
───────────────────────────────────────────────────────────────────────── */
function buildTableBody(lines) {
  const rows = [];
  (lines || []).forEach(line => {
    if (line.type === 'note') {
      if (line.description) rows.push([{
        content: line.description, colSpan: 7,
        styles: { halign: 'left', fontStyle: 'italic', textColor: [80,80,80], fillColor: [255,252,240], fontSize: 8, cellPadding: { top: 2.5, bottom: 2.5, left: 6, right: 3 } },
      }]);
      return;
    }
    const depthStr  = line.depth ? ' x ' + line.depth + '"' : '';
    const size      = line.width && line.height
      ? line.width + '" x ' + line.height + '"' + depthStr
      : (line.width ? line.width + '"' : line.height ? line.height + '"' : '');
    const unitPrice = parseFloat(line.price) || 0;
    const total     = unitPrice * (parseFloat(line.qty) || 1);
    const fmt       = (n) => n > 0 ? fmtMoney(n) : '';
    if (line.type === 'adder') {
      rows.push([
        { content: line.qty || '1' },
        { content: line.description || '', colSpan: 4, styles: { halign: 'left', fontStyle: 'italic', textColor: [40,100,40] } },
        { content: fmt(unitPrice), styles: { halign: 'right' } },
        { content: fmt(total),     styles: { halign: 'right' } },
      ]);
      return;
    }
    rows.push([line.qty || '1', line.description || '', line.room || '', size, line.ui || '', fmt(unitPrice), fmt(total)]);
  });
  return rows;
}

/* ─────────────────────────────────────────────────────────────────────────
   MAIN BUILD FUNCTION
───────────────────────────────────────────────────────────────────────── */
async function buildEstimatePDF(estimate, salesRep = {}) {
  const repName      = salesRep.displayName       || 'A.J. Gingerich';
  const repPhone     = salesRep.displayPhone      || '605-432-1834';
  const repEmail     = salesRep.email             || 'aj@thewindowplacellc.com';
  const repCellPhone = salesRep.notificationPhone || '';
  estimate._repName  = repName;
  estimate._repPhone = repPhone;

  const logo      = await getLogo();
  const c         = estimate.customer || {};
  const tableBody = buildTableBody(estimate.lines);

  const PH  = 99; // placeholder page count — fixed at the end
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });

  // ── Page 1: full header + customer box ──────────────────────────────────
  drawHeader(doc, estimate, logo, 1, PH);
  drawFooter(doc, 1, PH);

  let y = HEADER_H + 4;
  y = drawCustomerBox(doc, y, c, repName, repPhone, repEmail);

  let currentPage          = 1;
  let page1TableEndY       = null; // actual Y where table ended on page 1
  let tableSpilledToPage2  = false;

  // ── Table ────────────────────────────────────────────────────────────────
  // Reserve TABLE_BOTTOM_RESERVE mm at the bottom of every page.
  // This guarantees the totals block (TOTALS_H) always fits on the same
  // page as the last table row — so the total is NEVER orphaned alone.
  autoTable(doc, {
    startY: y,
    head: [['QTY', 'DESCRIPTION', 'ROOM', 'SIZE', 'UI', 'UNIT PRICE', 'TOTAL']],
    body: tableBody,
    styles: {
      fontSize: 8, cellPadding: { top: 2.8, bottom: 2.8, left: 2.5, right: 2.5 },
      textColor: DARK, lineColor: LIGHT_GRAY, lineWidth: 0.2, overflow: 'linebreak',
    },
    headStyles: {
      fillColor: TEAL_BG, textColor: TEAL_TEXT, fontStyle: 'bold',
      fontSize: 7, halign: 'center', valign: 'middle', minCellHeight: 8,
    },
    alternateRowStyles: { fillColor: [240, 250, 248] },
    columnStyles: {
      0: { cellWidth: 12,     halign: 'center' },
      1: { cellWidth: 'auto'                   },
      2: { cellWidth: 22,     halign: 'center' },
      3: { cellWidth: 30,     halign: 'center' },
      4: { cellWidth: 20,     halign: 'center' },
      5: { cellWidth: 26,     halign: 'right'  },
      6: { cellWidth: 26,     halign: 'right', fontStyle: 'bold', textColor: BRAND_BROWN },
    },
    // Key: margin.bottom reserves enough room so totals always stay with last row
    margin: { left: MARGIN, right: MARGIN, bottom: TABLE_BOTTOM_RESERVE },
    didDrawPage: (data) => {
      currentPage = data.pageNumber;
      if (data.pageNumber === 1) {
        // Record where the table ended on page 1
        page1TableEndY = data.cursor?.y ?? null;
      }
      if (data.pageNumber > 1) {
        tableSpilledToPage2 = true;
        drawContinuationHeader(doc, estimate, logo, data.pageNumber, PH);
        drawFooter(doc, data.pageNumber, PH);
      }
    },
  });

  const tableEndY = doc.lastAutoTable.finalY;

  // ── Totals + notes + after-section ──────────────────────────────────────
  // Because of the reserved margin, there is always room for totals here.
  y = tableEndY + 6;
  y = drawTotals(doc, y, estimate);
  y = drawNotes(doc, y, estimate);

  // Only draw the after-section if there's room — never force a new page for it
  if (y + AFTER_H < SAFE_Y) {
    drawAfterSection(doc, y, repName, repEmail, repCellPhone);
  }

  // ── Fix all pages with real page count ──────────────────────────────────
  const totalPages = doc.getNumberOfPages();

  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    if (p === 1) {
      drawHeader(doc, estimate, logo, p, totalPages);
      drawFooter(doc, p, totalPages);
      // If the table spilled to page 2, draw the slim grand total bar
      // at the bottom of page 1 so the customer never has to flip for the total
      if (tableSpilledToPage2) {
        drawSlimTotalBar(doc, estimate);
      }
    } else {
      drawContinuationHeader(doc, estimate, logo, p, totalPages);
      drawFooter(doc, p, totalPages);
    }
  }

  return doc.output('blob');
}

/* ─────────────────────────────────────────────────────────────────────────
   EXPORTS
───────────────────────────────────────────────────────────────────────── */
export async function generateQuotePDF(estimate, salesRep = {}, returnBlob = false, contractNumber = null) {
  const blob = await buildEstimatePDF(estimate, salesRep);
  const cn = contractNumber || estimate.contractNumber || null;
  if (cn) uploadJobPDF(cn, 'estimate', blob).catch(e => console.warn('PDF storage save failed:', e));
  if (returnBlob) return blob;
  const safeName = (estimate.customer?.name || 'Customer').replace(/[^a-zA-Z0-9 ]/g, '').trim();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = safeName + '.pdf';
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(a.href);
}

export async function generateQuotePDFBlob(estimate, salesRep = {}, contractNumber = null) {
  const blob = await buildEstimatePDF(estimate, salesRep);
  const cn = contractNumber || estimate.contractNumber || null;
  if (cn) uploadJobPDF(cn, 'estimate', blob).catch(e => console.warn('PDF storage save failed:', e));
  return blob;
}